import { useCallback, useState } from "react";

import { parseFileName } from "@/utils/getExtensionFromMime";

interface UseFileDropZoneProps {
  accept?: string;
  onFilesDropped: (files: FileList) => void;
}

interface DropZoneProps {
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}

interface UseFileDropZoneReturn {
  dropZoneProps: DropZoneProps;
  isDragging: boolean;
}

export function useFileDropZone({
  accept,
  onFilesDropped,
}: UseFileDropZoneProps): UseFileDropZoneReturn {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // Checks if user is dropping accepted file types
        if (accept) {
          const allowedExtensions = accept.split(",");
          const dataTransfer = new DataTransfer();

          for (const file of e.dataTransfer.files) {
            let extension: string | undefined;

            try {
              const { ext } = parseFileName(file.name);

              extension = ext;
            } catch {
              // Fallback for invalid filenames
              extension = file.name.split(".").pop();
            }

            if (extension) {
              const fileExtension = `.${extension.toLowerCase()}`;

              if (allowedExtensions.includes(fileExtension)) {
                dataTransfer.items.add(file);
              }
            }
          }

          if (dataTransfer.files.length > 0) {
            onFilesDropped(dataTransfer.files);
          }
        } else {
          onFilesDropped(e.dataTransfer.files);
        }

        e.dataTransfer.clearData();
      }
    },
    [onFilesDropped, accept],
  );

  return {
    dropZoneProps: {
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    isDragging,
  };
}
